import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { TimeRange } from "@volca/ui/components/ui/time-range-picker";
import { toast } from "@volca/ui/lib/toast";
import { useReportCalendar } from "@/profitability/calendar/query";
import { DocumentChip } from "@/profitability/document-chip";
import { useReportOverview } from "@/profitability/overview/query";
import { useCreateReport } from "@/profitability/query";
import { ReportDocumentsPicker } from "@/profitability/report-documents-picker";
import type { NewRunPreset } from "@/profitability/routes";
import type {
  ProfitabilityDocument,
  ProfitabilityReport,
} from "@/profitability/types";
import { dateRangeError, reportError, runDates } from "@/profitability/utils";
import { GOAL_SLOT, RunInputCard } from "./run-input-card";
import {
  earlierPeriods,
  periodDates,
  periodRange,
  proposePeriod,
  shortPeriod,
} from "./run-period";
import { type PeriodChoice, RunPeriodChoice } from "./run-period-choice";
import { useRunDocuments } from "./use-run-documents";

export const NewRunSkeleton = ({ label }: { label: string }) => (
  <Stack gap={6} align="stretch" aria-busy role="status">
    <span className="sr-only">{label}</span>
    {Array.from({ length: 3 }, (_, card) => (
      <Skeleton key={card} className="h-36 w-full rounded-xl" aria-hidden />
    ))}
  </Stack>
);

export const NewRunForm = ({
  businessId,
  report,
  onCreated,
  onCancel,
  preset = null,
}: {
  businessId: string;
  report: ProfitabilityReport;
  onCreated: (runId: string) => void;
  onCancel: () => void;
  /**
   * The period to run and the input to add a file to, from a missing
   * figure's link (`?start=&end=&input=&from=`). The period already has a
   * run, so this one is filed as its rerun: the API decides whether it
   * stands for the period or waits to be chosen as a correction.
   */
  preset?: NewRunPreset | null;
}) => {
  const create = useCreateReport(businessId);
  const overview = useReportOverview(businessId, report.id);
  const last = overview.data?.run ? runDates(overview.data.run) : null;
  const periods = overview.data?.periods ?? [];
  // A missing figure's link names the period to update; else the next one.
  const [picked, setPicked] = useState<PeriodChoice | null>(null);
  const [custom, setCustom] = useState<TimeRange | null>(null);
  // The owner's own figures for the period, checked against the run once it completes.
  const [goal, setGoal] = useState<ProfitabilityDocument | null>(null);
  // Suggested once the last run is known; the owner's own choice wins.
  const calendar = useReportCalendar(businessId, report.id);
  const proposed = proposePeriod(last, calendar.data);
  const next = periodDates(proposed);
  const earlier = earlierPeriods(next, last, periods, calendar.data);
  const presetPeriod = preset
    ? periods.find(
        (period) =>
          period.run_id === preset.from ||
          (period.start_date === preset.start_date &&
            period.end_date === preset.end_date),
      )
    : undefined;
  const choice: PeriodChoice =
    picked ??
    (presetPeriod
      ? { kind: "period", runId: presetPeriod.run_id }
      : preset
        ? { kind: "custom" }
        : next
          ? { kind: "next" }
          : { kind: "custom" });
  const chosen =
    choice.kind === "period"
      ? periods.find((period) => period.run_id === choice.runId)
      : undefined;
  const before =
    choice.kind === "earlier"
      ? earlier.find((period) => period.start_date === choice.startDate)
      : undefined;
  const listed = chosen ?? before;
  const shown =
    choice.kind === "next"
      ? proposed
      : listed
        ? periodRange(listed)
        : (custom ?? (preset && !picked ? periodRange(preset) : null));
  // Files for a period that ran start with that period's files in place.
  const run = useRunDocuments(
    businessId,
    report,
    chosen
      ? {
          runId: chosen.run_id,
          except: presetPeriod === chosen ? preset?.input : null,
        }
      : null,
  );
  const wanted = (key: string) =>
    preset?.input === key
      ? `The figures missing from ${shortPeriod(preset)} come from this file.`
      : null;
  const dates = periodDates(shown);
  const datesProblem = dates ? dateRangeError(dates) : null;
  if (overview.isPending || calendar.isPending)
    return <NewRunSkeleton label="Loading the upload" />;

  return (
    <Stack className="w-full" gap={6} align="stretch">
      <RunPeriodChoice
        next={next}
        earlier={earlier}
        periods={periods}
        choice={choice}
        onChoiceChange={setPicked}
        custom={custom ?? (choice.kind === "custom" ? shown : null)}
        onCustomChange={(value) => {
          setPicked({ kind: "custom" });
          setCustom(value);
        }}
      />
      {run.slotted ? (
        run.slots.map((slot) => (
          <RunInputCard
            key={slot.key}
            businessId={businessId}
            slot={slot}
            document={run.polledSlots[slot.key] ?? null}
            onDocumentChange={(document) =>
              run.setSlotDocument(slot.key, document)
            }
            wanted={wanted(slot.key)}
            kept={run.restorable(slot.key)}
            onUseKept={() => run.restoreKept(slot.key)}
          />
        ))
      ) : (
        <Card className="flex min-w-0 flex-col gap-6 border-line p-6 shadow-none">
          <Stack gap={2}>
            <Text variant="title" render={<h2 />}>
              Documents
            </Text>
            <Text tone="muted">Add the documents the report reads.</Text>
          </Stack>
          {run.documents.length > 0 && (
            <Stack align="start" direction="row" gap={3} className="flex-wrap">
              {run.documents.map((document) => (
                <DocumentChip
                  key={document.id}
                  document={document}
                  onRemove={() => run.picker.removeDocument(document.id)}
                />
              ))}
            </Stack>
          )}
          <Stack align="start" gap={6} direction="row">
            <ReportDocumentsPicker
              uploading={run.picker.uploading}
              onFiles={run.picker.handleFiles}
            />
          </Stack>
        </Card>
      )}
      <RunInputCard
        businessId={businessId}
        slot={GOAL_SLOT}
        document={goal}
        onDocumentChange={setGoal}
      />
      {run.problem && <Text className="text-danger">{run.problem}</Text>}
      {datesProblem && <Text className="text-danger">{datesProblem}</Text>}
      {!run.problem && run.slotted && run.missing.length > 0 && (
        <Text tone="muted">
          Add {run.missing.map((slot) => slot.title).join(", ")}.
        </Text>
      )}
      {!run.problem && run.missing.length === 0 && run.reading.length > 0 && (
        <Text tone="muted">
          The files are still being read. You can run the report now: it waits
          for them, then checks them.
        </Text>
      )}
      <Stack align="start" direction="row" gap={2}>
        <Button
          disabled={
            !run.ready || !!run.problem || !!datesProblem || create.isPending
          }
          aria-busy={create.isPending}
          onClick={() =>
            create.mutate(
              {
                ...run.request,
                ...dates,
                ...(goal ? { goal_document_id: goal.id } : {}),
              },
              {
                onSuccess: (started) => onCreated(started.id),
                onError: (error) =>
                  toast.error("The report could not be started", {
                    description: reportError(error),
                  }),
              },
            )
          }
        >
          Run report
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};
